import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Link2, Loader2, Share2 } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { compact, label, metaFor } from "./cityMeta";

function Stat({ v, k }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 16, padding: "22px 24px" }}>
      <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 46, fontWeight: 600, lineHeight: 1.1 }}>{v}</div>
      <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 17, letterSpacing: 2, textTransform: "uppercase", opacity: 0.7, marginTop: 6 }}>{k}</div>
    </div>
  );
}

const ShareCard = ({ card, cardRef }) => {
  const { accent: m_accent, photo, kicker, title, subtitle, stats, chips, quote } = card;
  const m = { accent: m_accent, photo };
  const q = quote;
  return (
    <div ref={cardRef} style={{ width: 1080, height: 1080, background: "#1C1917", color: "#fff", fontFamily: "Georgia, serif", display: "flex", flexDirection: "column" }} data-testid={`${card.tid || "featured-cities"}-share-card`}>
      <div style={{ position: "relative", height: 430, background: m.accent }}>
        {m.photo && <img src={m.photo} crossOrigin="anonymous" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(28,25,23,1), rgba(0,0,0,0.15))" }} />
        <div style={{ position: "absolute", left: 60, bottom: 40, right: 60 }}>
          <div style={{ display: "inline-block", background: m.accent, fontFamily: "IBM Plex Mono, monospace", fontSize: 20, letterSpacing: 3, textTransform: "uppercase", padding: "6px 14px", borderRadius: 6 }}>{kicker}</div>
          <div style={{ fontSize: title.length > 12 ? 96 : 120, lineHeight: 1, marginTop: 18 }}>{title}</div>
          <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 24, opacity: 0.85, marginTop: 12 }}>{subtitle}</div>
        </div>
      </div>
      <div style={{ padding: "36px 60px 0", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        {stats.map(([v, k]) => <Stat key={k} v={v} k={k} />)}
      </div>
      <div style={{ padding: "26px 60px 0", display: "flex", gap: 14, flexWrap: "wrap" }}>
        {chips.map((t) => (
          <div key={t} style={{ border: `2px solid ${m.accent}`, borderRadius: 999, padding: "8px 18px", fontFamily: "IBM Plex Mono, monospace", fontSize: 20 }}>{t}</div>
        ))}
      </div>
      {card.match && (
        <div style={{ margin: "26px 60px 0", display: "flex", alignItems: "center", gap: 28, background: "#fff", color: "#1C1917", borderRadius: 18, padding: "20px 28px" }} data-testid={`${card.tid}-share-card-match`}>
          <div style={{ fontFamily: "IBM Plex Mono, monospace", textAlign: "center" }}>
            <div style={{ fontSize: 16, letterSpacing: 2, textTransform: "uppercase", opacity: 0.6 }}>Your match</div>
            <div style={{ fontSize: 60, fontWeight: 600, lineHeight: 1, color: m.accent }}>{card.match.score}%</div>
          </div>
          <div style={{ fontSize: 24, lineHeight: 1.35 }}>
            {card.match.reasons.map((r) => <div key={r}>· {r}</div>)}
          </div>
        </div>
      )}
      {q && <div style={{ padding: "26px 60px 0", fontSize: 30, lineHeight: 1.3, fontStyle: "italic", opacity: 0.95 }}>“{q.quote.length > 150 ? `${q.quote.slice(0, 147)}…` : q.quote}” <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 18, fontStyle: "normal", opacity: 0.6 }}>— {q.source}</span></div>}
      <div style={{ marginTop: "auto", padding: "0 60px 40px", display: "flex", justifyContent: "space-between", alignItems: "flex-end", fontFamily: "IBM Plex Mono, monospace", fontSize: 20, opacity: 0.75 }}>
        <span>Which University Advisor</span><span>{window.location.host}</span>
      </div>
    </div>
  );
};

const btn = "flex w-full items-center gap-2 rounded px-2 py-2 text-sm text-stone-800 hover:bg-stone-100 transition-colors";

export function ShareCity({ c }) {
  const { formatFrom } = useCurrency();
  const m = metaFor(c);
  const card = {
    accent: m.accent, photo: m.photo, kicker: `Featured city · ${c.country}`, title: c.name,
    subtitle: `${compact(c.population)} residents · ${compact(c.student_population)} students`,
    stats: [
      [c.study.universities, "Universities"], [c.study.courses.toLocaleString(), "Courses"],
      [c.living.student_budget_mid ? formatFrom(c.living.student_budget_mid, c.living.currency) : "—", "Student budget / mo"],
      [c.study.avg_starting_salary_usd ? formatFrom(c.study.avg_starting_salary_usd, "USD") : "—", "Avg grad salary"],
    ],
    chips: c.industry.sectors.slice(0, 3).map((s) => `${label(s.tag)} ${s.rank ? `#${s.rank} of ${s.of}` : `${s.score}/10`}`),
    quote: c.quotes[0],
  };
  return <ShareMenu name={c.slug} title={c.name} card={card} url={`${window.location.origin}/?city=${c.slug}`}
    text={`${c.name}: ${c.study.universities} universities, ${c.study.courses.toLocaleString()} courses. Explore it on Which University Advisor`} label="Share this city" />;
}

export function ShareMenu({ name, title, card, url, text, label: buttonLabel, tid = "featured-cities" }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const c = { slug: name, name: title };
  const enc = encodeURIComponent;
  const makePng = () => toPng(cardRef.current, { width: 1080, height: 1080, pixelRatio: 1, cacheBust: true });

  const download = async () => {
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = await makePng();
      a.download = `wua-${c.slug}.png`;
      a.click();
    } catch { toast.error("Could not create the image"); } finally { setBusy(false); }
  };
  const nativeShare = async () => {
    try {
      const blob = await (await fetch(await makePng())).blob();
      const file = new File([blob], `wua-${c.slug}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: c.name, text, url });
      else await navigator.share({ title: c.name, text, url });
    } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing isn't available here"); }
  };
  const copy = async () => { await navigator.clipboard.writeText(url); toast.success("Link copied"); };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center justify-between rounded-full border border-white/40 px-5 py-3 text-sm transition-colors hover:bg-white/10" data-testid={`${tid}-share-button`}>
            {buttonLabel} <Share2 className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" data-testid={`${tid}-share-menu`}>
          <button type="button" onClick={download} disabled={busy} className={btn} data-testid={`${tid}-share-download`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download image (1080×1080)
          </button>
          {typeof navigator !== "undefined" && navigator.share && (
            <button type="button" onClick={nativeShare} className={btn} data-testid={`${tid}-share-native`}><Share2 className="h-4 w-4" /> Share…</button>
          )}
          <a className={btn} target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`} data-testid={`${tid}-share-x`}>Share on X</a>
          <a className={btn} target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`} data-testid={`${tid}-share-linkedin`}>Share on LinkedIn</a>
          <a className={btn} target="_blank" rel="noreferrer" href={`https://wa.me/?text=${enc(`${text} ${url}`)}`} data-testid={`${tid}-share-whatsapp`}>Share on WhatsApp</a>
          <button type="button" onClick={copy} className={btn} data-testid={`${tid}-share-copy`}><Link2 className="h-4 w-4" /> Copy link</button>
        </PopoverContent>
      </Popover>
      {createPortal(
        <div aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}>
          <ShareCard card={card} cardRef={cardRef} />
        </div>,
        document.body,
      )}
    </>
  );
}
